<!-- Global notification container -->
<xemex-notification-container />

<xemex-page-header
  [title]="'sidebarMenu.dataExport' | translate"
  [breadcrumbs]="breadcrumbs"
>
  @if (allChips().length > 0) {
    <!-- NOSONAR S6853 - button attribute, not form label -->
    <xemex-outlined-button
      actions
      type="button"
      icon="delete"
      (click)="clearAllSelection()"
    >
      {{ 'dataExport.clearSelection' | translate }}
    </xemex-outlined-button>
  }
</xemex-page-header>

<xemex-page-body [scrollable]="true">
  <div class="max-w-5xl mx-auto p-6 flex flex-col gap-6">
    @if (!hasDevices()) {
      <div class="data-export__empty">
        <p>{{ 'dataExport.noDevices' | translate }}</p>
      </div>
    } @else {
      <xemex-card [title]="'dataExport.newExport.title' | translate">
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <label for="deviceOrGroupSearchInput" class="data-export__label">
              {{ 'dataExport.devicesOrGroupLabel' | translate }}
            </label>

            <div class="flex items-center gap-2">
              <div class="relative flex-1" (focusin)="onSearchFocus()" (focusout)="onSearchBlur()">
                <xemex-search-input
                  [value]="searchTerm()"
                  [placeholder]="'dataExport.searchPlaceholder' | translate"
                  (valueChange)="onSearchInputChange($event)"
                />
                @if (isSearchOpen() && searchTerm() && (locationResults().length || groupResults().length)) {
                  <ul class="data-export__results absolute z-10 mt-1 w-full max-h-64 overflow-auto">
                    @if (locationResults().length) {
                      <li class="data-export__result-header px-3 py-1">
                        <mat-icon class="data-export__result-header-icon">home_work</mat-icon>
                        {{ 'dataExport.searchSections.locations' | translate }}
                      </li>
                      @for (location of locationResults().slice(0, 5); track location.locationId) {
                        <li
                          class="data-export__result px-3 py-2"
                          (mousedown)="$event.preventDefault()"
                          (click)="onLocationOptionSelected(location)"
                        >
                          <mat-icon class="data-export__result-icon">home_work</mat-icon>
                          {{ locationAddress(location) }}
                        </li>
                      }
                    }
                    @if (groupResults().length) {
                      <li class="data-export__result-header px-3 py-1">
                        <mat-icon class="data-export__result-header-icon">workspaces</mat-icon>
                        {{ 'dataExport.searchSections.installationGroups' | translate }}
                      </li>
                      @for (group of groupResults().slice(0, 5); track group.installationGroupOid) {
                        <li
                          class="data-export__result px-3 py-2"
                          (mousedown)="$event.preventDefault()"
                          (click)="onGroupOptionSelected(group.installationGroupOid)"
                        >
                          <mat-icon class="data-export__result-icon">workspaces</mat-icon>
                          {{ group.name }}
                        </li>
                      }
                    }
                  </ul>
                }
              </div>

              <button
                type="button"
                mat-icon-button
                [disabled]="!hasDevices()"
                [title]="'dataExport.devicesMenu' | translate"
                (click)="openDeviceSelectionDialog()"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
            </div>

            @if (allChips().length) {
              <div class="flex flex-col gap-2">
                <mat-chip-set class="data-export__chips" [attr.aria-label]="'dataExport.devicesOrGroupLabel' | translate">
                  @for (item of visibleChips(); track item.type + (item.type === 'device' ? item.chip.id : item.type === 'location' ? item.chip.id : item.chip.oid)) {
                    @switch (item.type) {
                      @case ('device') {
                        <mat-chip (removed)="removeDeviceChip(item.chip.id)">
                          {{ item.chip.label }}
                          <button matChipRemove [attr.aria-label]="'Remove ' + item.chip.label">
                            <mat-icon>cancel</mat-icon>
                          </button>
                        </mat-chip>
                      }
                      @case ('location') {
                        <mat-chip (removed)="removeLocationChip(item.chip.id)">
                          {{ 'dataExport.locationChipLabel' | translate: { name: item.chip.label } }}
                          <button matChipRemove [attr.aria-label]="'Remove ' + item.chip.label">
                            <mat-icon>cancel</mat-icon>
                          </button>
                        </mat-chip>
                      }
                      @case ('group') {
                        <mat-chip (removed)="removeGroupChip(item.chip.oid)">
                          {{ 'dataExport.groupChipLabel' | translate: { name: item.chip.name } }}
                          <button matChipRemove [attr.aria-label]="'Remove ' + item.chip.name">
                            <mat-icon>cancel</mat-icon>
                          </button>
                        </mat-chip>
                      }
                    }
                  }
                </mat-chip-set>
                @if (hasMoreChips()) {
                  <button
                    type="button"
                    class="data-export__show-more-btn"
                    (click)="isChipsExpanded.set(!isChipsExpanded())"
                  >
                    @if (isChipsExpanded()) {
                      {{ 'dataExport.showLess' | translate }}
                    } @else {
                      {{ 'dataExport.showMore' | translate: { count: allChips().length - CHIPS_DISPLAY_LIMIT } }}
                    }
                  </button>
                }
                @if (isResolvingSelection()) {
                  <p class="data-export__hint text-sm">
                    <mat-icon class="inline align-middle mr-1">hourglass_top</mat-icon>
                    {{ 'dataExport.resolvingSelection' | translate }}
                  </p>
                }
              </div>
            }
          </div>

          <div class="flex flex-col gap-2">
            <h3 class="data-export__label">
              {{ 'dataExport.energyPresets.label' | translate }}
            </h3>
            <div class="data-export__presets">
              @for (preset of availablePresets; track preset.value) {
                <button
                  type="button"
                  class="data-export__preset"
                  [class.data-export__preset--selected]="selectedEnergyPreset() === preset.value"
                  (click)="selectEnergyPreset(preset.value)"
                >
                  @if (selectedEnergyPreset() === preset.value) {
                    <mat-icon class="data-export__preset-icon">check_circle</mat-icon>
                  } @else {
                    <mat-icon class="data-export__preset-icon">radio_button_unchecked</mat-icon>
                  }
                  {{ preset.labelKey | translate }}
                </button>
              }
            </div>
          </div>

          <form [formGroup]="exportForm" class="flex flex-col gap-4">
            <fieldset class="flex flex-col gap-2 border-0 p-0 m-0">
              <legend class="data-export__label p-0">
                {{ 'dataExport.granularity.label' | translate }}
              </legend>
              <xemex-button-toggle formControlName="granularity" [options]="granularityOptions()" />
            </fieldset>

            <fieldset class="flex flex-col gap-2 border-0 p-0 m-0">
              <legend class="data-export__label p-0">
                {{ 'dataExport.periodLabel' | translate }}
              </legend>
              <div class="flex items-center gap-3">
                <xemex-date-field
                  class="flex-1"
                  [label]="'devices.csvExport.startDate' | translate"
                  formControlName="startDate"
                  [max]="maxDate"
                />
                <span class="data-export__separator">→</span>
                <xemex-date-field
                  class="flex-1"
                  [label]="'devices.csvExport.endDate' | translate"
                  formControlName="endDate"
                  [max]="maxDate"
                />
              </div>
              <p class="data-export__hint">
                {{ 'dataExport.periodHint' | translate: { maxYears: maxRangeYearsForSelectedGranularity() } }}
              </p>
            </fieldset>
          </form>

          @if (isRangeInvalid()) {
            <p class="data-export__error">{{ 'devices.csvExport.dateRangeError' | translate }}</p>
          }
          @if (isRangeTooLong()) {
            <p class="data-export__error">
              {{ 'dataExport.rangeTooLongError' | translate: { maxDays: maxRangeDaysForSelectedGranularity() } }}
            </p>
          }
          @if (hasNoSelection() && !isResolvingSelection()) {
            <p class="data-export__error">{{ 'dataExport.noSelectionError' | translate }}</p>
          }
          @if (selectionError(); as error) {
            <p class="data-export__error">{{ error }}</p>
          }
          @if (submitError(); as error) {
            <p class="data-export__error">{{ error }}</p>
          }

          <div class="flex justify-end">
            <xemex-filled-button
              type="button"
              icon="download"
              [loading]="isSubmitting()"
              [disabled]="!canExport()"
              (click)="onExport()"
            >
              {{ 'dataExport.exportButton' | translate }}
            </xemex-filled-button>
          </div>
        </div>
      </xemex-card>

      <xemex-card [title]="'dataExport.history.title' | translate">
        <p class="data-export__hint flex items-center gap-1">
          <mat-icon class="data-export__datapoint-icon" [matTooltip]="'dataExport.history.retentionInfo' | translate">
            info
          </mat-icon>
          {{ 'dataExport.history.retentionInfo' | translate }}
        </p>
        <xemex-table [data]="historyRows()" [columns]="historyColumns" [pageable]="false" [loading]="false">
          <ng-template #actionsCell let-row>
            @if (row.downloadAvailable) {
              <button
                mat-icon-button
                (click)="onHistoryRowDownload(row); $event.stopPropagation()"
                [title]="'dataExport.history.download' | translate"
              >
                <mat-icon>download</mat-icon>
              </button>
            }
          </ng-template>
        </xemex-table>
      </xemex-card>
    }
  </div>
</xemex-page-body>
